import { defineComponent, ExtractPropTypes, computed, ref } from 'vue';
import { createNamespaceBem } from '@/utils';
import { useParent } from '@vant/use';
import { STEPS_KEY } from '@/components/steps/Steps';
import { useElementBounding } from '@vueuse/core';
import { t } from '@/assets/theme';
import { Icon } from 'vant';

const { name, bem } = createNamespaceBem('step');

const stepProps = {};

export type StepProps = ExtractPropTypes<typeof stepProps>;

/**
 * node
 *    line
 *    circle => icon | text
 * info
 *    title
 *    desc
 */
export default defineComponent({
  name,

  setup(props, { slots }) {
    const { parent, index } = useParent(STEPS_KEY);
    const stepDom = ref(null);
    const { width: stepDomW } = useElementBounding(stepDom);
    const circleDom = ref(null);
    const { width: circleDomW } = useElementBounding(circleDom);

    // 判断必须是Steps的子组件
    if (!parent) {
      console.error('[Fda] <Step> 组件必须是在 <Steps> 组件内调用！');
      return;
    }

    const parentProps = parent.props;

    const onClickStep = () => parent.onClickStep(index.value);

    // 计算当前状态
    const enum StatusEnum {
      finish = 'finish',
      process = 'process',
      waiting = 'waiting',
    }

    const getStatus = (): StatusEnum => {
      const active = +parentProps.active;
      if (index.value < active) {
        return StatusEnum.finish;
      }
      return index.value === active ? StatusEnum.process : StatusEnum.waiting;
    };

    const isActive = () => getStatus() === StatusEnum.process;

    const lineStyle = computed(() => ({
      borderColor:
        // eslint-disable-next-line no-nested-ternary
        getStatus() === StatusEnum.finish
          ? parentProps.finishColor
          : getStatus() === StatusEnum.process
          ? parentProps.processColor
          : parentProps.waitingColor,
      width: `${stepDomW.value - circleDomW.value * 2}px`,
      left: `${stepDomW.value / 2 + circleDomW.value}px`,
    }));

    const renderCircle = () => {
      const { iconPrefix, finishIcon, processIcon, waitingIcon } = parentProps;
      switch (getStatus()) {
        case StatusEnum.finish:
          if (slots['finish-node']) {
            return slots['finish-node']();
          }
          if (finishIcon) {
            return <Icon classPrefix={iconPrefix} name={finishIcon} size={t(8)} />;
          }
          break;
        case StatusEnum.process:
          if (slots['process-node']) {
            return slots['process-node']();
          }
          if (processIcon) {
            return <Icon classPrefix={iconPrefix} name={processIcon} size={t(8)} />;
          }
          break;
        case StatusEnum.waiting:
        default:
          if (slots['waiting-node']) {
            return slots['waiting-node']();
          }
          if (waitingIcon) {
            return <Icon classPrefix={iconPrefix} name={waitingIcon} size={t(8)} />;
          }
          break;
      }
      if (slots.node) {
        return slots.node();
      }
      return <span>{index.value + 1}</span>;
    };

    return () => {
      return (
        <div
          ref={stepDom}
          onClick={onClickStep}
          class={bem([parentProps.direction, getStatus(), { active: isActive() }])}
        >
          {/* node */}
          <div class={bem('node')}>
            {/* line */}
            <div class={bem('line')} style={lineStyle.value} />
            {/* circle */}
            <div ref={circleDom} class={bem('circle')}>
              {renderCircle()}
            </div>
          </div>
          {/* info */}
          <div class={bem('info')}>{slots.default?.()}</div>
        </div>
      );
    };
  },
});
